import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../Common/_hooks/useAppDispatch';
import { FetchUserStorePages } from '../../../UserData/_actions/Store/UserStorePageActions';
import { ResetProductListings } from '../../../UserData/_actions/Store/StoreProductListingActions';
import Dialog from '../../../Common/Dialog/Dialog';
import DialogTitle from '../../../Common/Dialog/DialogTitle';
import DialogContent from '../../../Common/Dialog/DialogContent';
import DialogActions from '../../../Common/Dialog/DialogActions';
import DialogButton from '../../../Common/Buttons/DialogButton';
import SelectProduct from './SelectProduct';
import EditSelectedProducts from './EditSelectedProducts';
import EditProduct from './EditProduct';
import Publishing from './Publishing';

interface Props {
	opened: boolean
	store: string
	onClose: () => void
}

const STEPS = ['SelectProduct', 'EditSelectedProducts', 'EditDetails', 'Publishing'];

export default function PublishProductsToStoreDialog(props: Props) {
	const [t] = useTranslation('account-v2')
	const dispatch = useAppDispatch()

	const [currentStep, setCurrentStep] = useState(0)
	const [closeDialogOpened, setCloseDialogOpened] = useState(false)
	const [publishBatchId, setPublishBatchId] = useState('')

	useEffect(() => {
		if (props.store !== 'squarespace') return;

		dispatch(FetchUserStorePages(props.store))
	}, [dispatch, props.store])

	const setStep = useCallback((step: number) => {
		setCurrentStep(step)
	}, [])

	const resetDialogState = useCallback(() => {
		setStep(0)
		setPublishBatchId('')
		dispatch(ResetProductListings())
	}, [dispatch, setStep])

	const handleCloseModal = useCallback(() => {
		setCloseDialogOpened(false)
		props.onClose()
	}, [props.onClose])

	const onCloseDialogOpen = useCallback(() => setCloseDialogOpened(true), [])
	const onCloseDialogClose = useCallback(() => setCloseDialogOpened(false), [])

	useEffect(() => {
		if (!props.opened) {
			return
		}

		resetDialogState()
	}, [props.opened, resetDialogState])

	const onDialogClose = useCallback(() => {
		if (currentStep > 0 && currentStep < STEPS.length - 1) {
			onCloseDialogOpen()
		} else {
			handleCloseModal()
		}
	}, [currentStep, onCloseDialogOpen, handleCloseModal])

	const body = useMemo(() => {
		switch (currentStep) {
			case 0:
				return <SelectProduct
					index={currentStep}
					store={props.store}
					handleStep={setStep}
					onDialogClose={onDialogClose}
				/>
			case 1:
				return <EditSelectedProducts
					store={props.store}
					index={currentStep}
					handleStep={setStep}
					onPublishStarted={setPublishBatchId}
					onDialogClose={onDialogClose}
				/>
			case 2:
				return <EditProduct
					store={props.store}
					index={currentStep}
					handleStep={setStep}
					onDialogClose={onDialogClose}
					onDialogCloseWithConfirm={onDialogClose}
				/>
			case 3:
				return <Publishing
					store={props.store}
					batchId={publishBatchId}
					onDialogClose={onDialogClose}
				/>
		}
	}, [props.store, currentStep, onDialogClose, setStep, publishBatchId])

	const isPublishingStep = currentStep === STEPS.length - 1

	return <>
		<Dialog
			open={props.opened}
			maxWidth={isPublishingStep ? 'sm' : 'lg'}
			fullWidth
			onClose={onDialogClose}
			TransitionProps={{
				onExited: resetDialogState,
			}}
			showCloseButton
			hasTabs={currentStep === 2}
		>
			{body}
		</Dialog>

		<Dialog
			open={closeDialogOpened}
			maxWidth="xs"
			fullWidth
			onClose={onCloseDialogClose}
			showCloseButton
		>
			<DialogTitle>{t('Are you sure?')}</DialogTitle>
			<DialogContent>
				<p>{t('You will lose your changes if you close this dialog.')}</p>
			</DialogContent>
			<DialogActions>
				<DialogButton
					color="secondary"
					onClick={onCloseDialogClose}
				>{t('Cancel')}</DialogButton>
				<DialogButton
					color="primary"
					onClick={handleCloseModal}
					autoFocus
				>{t('Close')}</DialogButton>
			</DialogActions>
		</Dialog>
	</>
}
